import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import SEO from '../components/shared/SEO';

/**
 * Terms & Conditions Page
 */
export default function Terms() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const element = document.querySelector(hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [hash]);

  return (
    <div className="bg-midnight min-h-screen">
      <SEO
        title="Terms & Conditions"
        description="Read Raflr's terms and conditions for using our skill-based competition marketplace. Rules for buyers and sellers in Ireland."
        path="/terms"
      />
      <div className="page-container py-12 md:py-20">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-3xl md:text-4xl font-display text-cream mb-4">
            Terms & Conditions
          </h1>
          <p className="text-cream/50 mb-12">
            Last updated: 20 January 2026
          </p>

          <div className="prose prose-invert prose-cream max-w-none space-y-8">
            {/* Section 1 */}
            <section id="definitions">
              <h2 className="text-xl font-semibold text-cream mb-4">1. Definitions and Interpretation</h2>

              <p className="text-cream/70 mb-4">
                <strong className="text-cream">1.1 The Platform:</strong> Raflr ("we", "us", "the Platform") operates a marketplace for skill-based competitions where Sellers list items and Buyers enter competitions to acquire them. The Platform is accessible at raflr.ie (the "Website").
              </p>

              <p className="text-cream/70 mb-4">
                <strong className="text-cream">1.2 Definitions:</strong> In these Terms and Conditions:
              </p>
              <ul className="list-disc list-inside text-cream/70 space-y-2 ml-4">
                <li><strong className="text-cream">"Competition"</strong> means a skill-based contest hosted by a Seller on the Platform where Buyers can enter for a chance to acquire the listed item.</li>
                <li><strong className="text-cream">"Entry"</strong> means a valid submission by a Buyer to participate in a Competition, requiring demonstration of skill and payment of the Entry fee.</li>
                <li><strong className="text-cream">"Seller"</strong> means a registered user who lists items for Competition on the Platform.</li>
                <li><strong className="text-cream">"Buyer"</strong> means a registered user who enters Competitions on the Platform.</li>
                <li><strong className="text-cream">"Winner"</strong> means the Buyer selected to receive the item upon successful completion of a Competition.</li>
                <li><strong className="text-cream">"Item"</strong> or <strong className="text-cream">"Prize"</strong> means the physical goods listed by a Seller for a Competition.</li>
                <li><strong className="text-cream">"Skill Question"</strong> means the question that must be answered correctly to complete an Entry.</li>
                <li><strong className="text-cream">"Handover"</strong> means the physical transfer of the Item from Seller to Winner.</li>
              </ul>

              <p className="text-cream/70 mt-4">
                <strong className="text-cream">1.3 Legal Classification:</strong> Competitions on Raflr are skill-based prize competitions, not lotteries or gambling. Entry requires demonstrating skill by correctly answering a question. This classification is in accordance with Irish competition law.
              </p>
            </section>

            {/* Section 2 */}
            <section id="eligibility">
              <h2 className="text-xl font-semibold text-cream mb-4">2. Eligibility</h2>

              <p className="text-cream/70 mb-4">
                <strong className="text-cream">2.1 Age Requirement:</strong> Competitions are open to individuals who are at least eighteen (18) years of age and who have reached the age of majority in their jurisdiction of residence at the time of Entry.
              </p>

              <p className="text-cream/70 mb-4">
                <strong className="text-cream">2.2 Geographic Restrictions:</strong> The Platform is primarily intended for users located in Ireland. Sellers must be located in Ireland and able to facilitate local pickup of Items. Buyers entering Competitions must be able to collect Items locally.
              </p>

              <p className="text-cream/70 mb-4">
                <strong className="text-cream">2.3 Seller Restrictions:</strong> Sellers may not enter their own Competitions. Immediate family members of Sellers (spouse, parent, child, sibling) may not enter Competitions hosted by that Seller.
              </p>

              <p className="text-cream/70 mb-4">
                <strong className="text-cream">2.4 Account Termination:</strong> We reserve the right to suspend or terminate any account without notice if, in our sole discretion, the user engages in improper conduct, fraudulent activity, abusive behaviour, or any action detrimental to other users or the Platform.
              </p>

              <p className="text-cream/70">
                <strong className="text-cream">2.5 Acceptance of Terms:</strong> By using the Platform, you confirm that you meet all eligibility requirements and agree to be bound by these Terms and Conditions.
              </p>
            </section>

            {/* Section 3 */}
            <section id="accounts">
              <h2 className="text-xl font-semibold text-cream mb-4">3. Account Registration</h2>

              <p className="text-cream/70 mb-4">
                <strong className="text-cream">3.1 Account Requirement:</strong> To enter Competitions or list Items, you must register an account by providing a valid email address and verifying your phone number.
              </p>

              <p className="text-cream/70 mb-4">
                <strong className="text-cream">3.2 Phone Verification:</strong> Phone verification is mandatory for all users who wish to enter Competitions or list Items. This helps ensure the security and integrity of the Platform.
              </p>

              <p className="text-cream/70 mb-4">
                <strong className="text-cream">3.3 Single Account Policy:</strong> Each individual may operate only one (1) Raflr account. Multiple accounts associated with a single user will be terminated, and any Entries or listings may be voided.
              </p>

              <p className="text-cream/70 mb-4">
                <strong className="text-cream">3.4 Account Security:</strong> You are responsible for maintaining the confidentiality of your account credentials. You must notify us immediately of any unauthorised use of your account.
              </p>

              <p className="text-cream/70">
                <strong className="text-cream">3.5 Seller Verification:</strong> Sellers must complete additional verification through Stripe Connect to receive payments. This includes providing banking details and identity verification as required by Stripe.
              </p>
            </section>

            {/* Section 4 */}
            <section id="competitions">
              <h2 className="text-xl font-semibold text-cream mb-4">4. Competition Rules</h2>

              <p className="text-cream/70 mb-4">
                <strong className="text-cream">4.1 Skill-Based Entry:</strong> To enter a Competition, Buyers must correctly answer a skill question. This requirement ensures Raflr operates as a legitimate skill-based competition under Irish law. Incorrect answers may be retried until the correct answer is provided.
              </p>

              <p className="text-cream/70 mb-4">
                <strong className="text-cream">4.2 Entry Limits:</strong> Buyers may purchase multiple Entries to a single Competition, subject to the following limits:
              </p>
              <ul className="list-disc list-inside text-cream/70 space-y-1 ml-4 mb-4">
                <li>Maximum 20% of total Entries available, OR</li>
                <li>Maximum 10 Entries per Competition</li>
                <li>Whichever is lower applies</li>
              </ul>

              <p className="text-cream/70 mb-4">
                <strong className="text-cream">4.3 Entry Confirmation:</strong> An Entry is only valid once payment is confirmed and you receive email confirmation. Entries are assigned sequential ticket numbers.
              </p>

              <p className="text-cream/70 mb-4">
                <strong className="text-cream">4.4 Competition Duration:</strong> Competitions run for between 1 and 30 days as specified by the Seller. The end date and time are displayed on each Competition page.
              </p>

              <p className="text-cream/70 mb-4">
                <strong className="text-cream">4.5 Minimum Threshold:</strong> Sellers set a minimum threshold (50-100% of Entries). If this threshold is not met by the end date, the Competition is cancelled and all Buyers receive automatic refunds.
              </p>

              <p className="text-cream/70 mb-4">
                <strong className="text-cream">4.6 Winner Selection:</strong> When a Competition ends and the minimum threshold is met, a Winner is selected automatically from all valid Entries. The Winner is notified immediately via email and SMS.
              </p>

              <p className="text-cream/70">
                <strong className="text-cream">4.7 Competition Cancellation:</strong> Sellers may cancel a Competition before it ends. If cancelled, all Buyers receive automatic refunds of their Entry fees.
              </p>
            </section>

            {/* Section 5 */}
            <section id="sellers">
              <h2 className="text-xl font-semibold text-cream mb-4">5. Seller Obligations</h2>

              <p className="text-cream/70 mb-4">
                <strong className="text-cream">5.1 Item Accuracy:</strong> Sellers must provide accurate descriptions and photos of Items. Items must match the condition and description provided in the listing.
              </p>

              <p className="text-cream/70 mb-4">
                <strong className="text-cream">5.2 Listing:</strong> There is no upfront fee to create a Competition. Competitions go live immediately upon creation.
              </p>

              <p className="text-cream/70 mb-4">
                <strong className="text-cream">5.3 Payment Setup:</strong> Sellers must connect a valid bank account via Stripe Connect before listing Items. Payments are made directly to the Seller's bank account.
              </p>

              <p className="text-cream/70 mb-4">
                <strong className="text-cream">5.4 Handover Obligation:</strong> Upon completion of a Competition, Sellers must make the Item available for collection by the Winner within a reasonable timeframe. Sellers must respond to Winner messages and arrange pickup.
              </p>

              <p className="text-cream/70 mb-4">
                <strong className="text-cream">5.5 Item Availability:</strong> Sellers warrant that they have legal ownership of the Item and the right to sell it. Items must be available for the duration of the Competition.
              </p>

              <p className="text-cream/70">
                <strong className="text-cream">5.6 Active Listing Limit:</strong> Sellers may have only one (1) active Competition at any time.
              </p>
            </section>

            {/* Section 6 */}
            <section id="payments">
              <h2 className="text-xl font-semibold text-cream mb-4">6. Payments and Refunds</h2>

              <p className="text-cream/70 mb-4">
                <strong className="text-cream">6.1 Payment Processing:</strong> All payments are processed securely by Stripe. We do not store or have access to your payment card details.
              </p>

              <p className="text-cream/70 mb-4">
                <strong className="text-cream">6.2 Escrow:</strong> Entry payments are held securely by Stripe until the Handover is confirmed by both parties. Funds are not released to the Seller until successful completion.
              </p>

              <p className="text-cream/70 mb-4">
                <strong className="text-cream">6.3 Automatic Refunds:</strong> Buyers receive automatic full refunds in the following circumstances:
              </p>
              <ul className="list-disc list-inside text-cream/70 space-y-1 ml-4 mb-4">
                <li>Competition does not meet its minimum threshold</li>
                <li>Seller cancels the Competition</li>
                <li>Dispute resolved in Buyer's favour</li>
                <li>Seller fails to complete Handover</li>
              </ul>

              <p className="text-cream/70 mb-4">
                <strong className="text-cream">6.4 Non-Refundable Entries:</strong> Entry fees are non-refundable once a Competition has successfully completed and a Winner has been selected, except in cases covered by clause 6.3.
              </p>

              <p className="text-cream/70 mb-4">
                <strong className="text-cream">6.5 Seller Payouts:</strong> Sellers receive payment after both parties confirm the Handover. Funds are transferred to the Seller's connected bank account, typically within 2 business days.
              </p>

              <p className="text-cream/70">
                <strong className="text-cream">6.6 Platform Fees:</strong> Raflr charges a 5% platform fee on successful Competitions only. This fee is deducted from the Seller's payout and covers payment processing, escrow protection, and dispute handling. There is no upfront listing fee.
              </p>
            </section>

            {/* Section 7 */}
            <section id="handover">
              <h2 className="text-xl font-semibold text-cream mb-4">7. Item Handover</h2>

              <p className="text-cream/70 mb-4">
                <strong className="text-cream">7.1 Winner Notification:</strong> Winners are notified immediately when a Competition ends. The Seller's pickup location is revealed to the Winner only.
              </p>

              <p className="text-cream/70 mb-4">
                <strong className="text-cream">7.2 Arranging Pickup:</strong> Winners and Sellers communicate via the Platform's messaging system to arrange a mutually convenient pickup time and location.
              </p>

              <p className="text-cream/70 mb-4">
                <strong className="text-cream">7.3 Confirmation Required:</strong> Both the Winner and Seller must confirm the Handover in the Platform before funds are released to the Seller. This protects both parties.
              </p>

              <p className="text-cream/70 mb-4">
                <strong className="text-cream">7.4 Inspection Rights:</strong> Winners have the right to inspect the Item at pickup to ensure it matches the listing description before confirming receipt.
              </p>

              <p className="text-cream/70 mb-4">
                <strong className="text-cream">7.5 Handover Timeframe:</strong> Handovers should be completed within a reasonable timeframe. If either party fails to respond or complete the Handover within 14 days, we may intervene to resolve the situation.
              </p>

              <p className="text-cream/70">
                <strong className="text-cream">7.6 Auto-Release:</strong> If one party confirms Handover and the other does not respond within 48 hours (and no dispute is raised), funds may be automatically released to the Seller.
              </p>
            </section>

            {/* Section 8 */}
            <section id="disputes">
              <h2 className="text-xl font-semibold text-cream mb-4">8. Disputes</h2>

              <p className="text-cream/70 mb-4">
                <strong className="text-cream">8.1 Raising a Dispute:</strong> Either party may raise a dispute if issues arise with the Handover. Disputes must be raised within 48 hours of any attempted Handover.
              </p>

              <p className="text-cream/70 mb-4">
                <strong className="text-cream">8.2 Valid Dispute Reasons:</strong> Disputes may be raised for:
              </p>
              <ul className="list-disc list-inside text-cream/70 space-y-1 ml-4 mb-4">
                <li>Item not as described</li>
                <li>Item not received</li>
                <li>Seller unresponsive</li>
                <li>Winner unresponsive</li>
                <li>Item damaged</li>
              </ul>

              <p className="text-cream/70 mb-4">
                <strong className="text-cream">8.3 Evidence:</strong> Parties may submit evidence (photos, messages) to support their dispute claim.
              </p>

              <p className="text-cream/70 mb-4">
                <strong className="text-cream">8.4 Resolution:</strong> We will review disputes and make a determination based on the evidence provided. Our decision is final. Resolutions may include:
              </p>
              <ul className="list-disc list-inside text-cream/70 space-y-1 ml-4 mb-4">
                <li>Full refund to Winner</li>
                <li>Full payment to Seller</li>
                <li>Partial resolution</li>
              </ul>

              <p className="text-cream/70">
                <strong className="text-cream">8.5 Dispute Hold:</strong> While a dispute is under review, funds remain held and are not released to either party.
              </p>
            </section>

            {/* Section 9 */}
            <section id="liability">
              <h2 className="text-xl font-semibold text-cream mb-4">9. Limitation of Liability</h2>

              <p className="text-cream/70 mb-4">
                <strong className="text-cream">9.1 Platform Role:</strong> Raflr is a marketplace platform that connects Sellers and Buyers. We are not party to the transaction between Seller and Winner.
              </p>

              <p className="text-cream/70 mb-4">
                <strong className="text-cream">9.2 Item Quality:</strong> We make no warranties regarding the quality, safety, or legality of Items listed on the Platform. Sellers are solely responsible for their Items.
              </p>

              <p className="text-cream/70 mb-4">
                <strong className="text-cream">9.3 Exclusions:</strong> To the maximum extent permitted by law, we exclude liability for:
              </p>
              <ul className="list-disc list-inside text-cream/70 space-y-1 ml-4 mb-4">
                <li>Indirect or consequential losses</li>
                <li>Loss of profits, revenue, or anticipated savings</li>
                <li>Loss arising from circumstances outside our reasonable control</li>
                <li>Actions or omissions of Sellers or Buyers</li>
              </ul>

              <p className="text-cream/70 mb-4">
                <strong className="text-cream">9.4 Maximum Liability:</strong> Our total liability to any user shall not exceed the total Entry fees paid by that user in the 12 months preceding any claim.
              </p>

              <p className="text-cream/70">
                <strong className="text-cream">9.5 Statutory Rights:</strong> Nothing in these Terms excludes or limits liability for death or personal injury caused by negligence, fraud, or any other liability that cannot be excluded by law.
              </p>
            </section>

            {/* Section 10 */}
            <section id="prohibited">
              <h2 className="text-xl font-semibold text-cream mb-4">10. Prohibited Items and Conduct</h2>

              <p className="text-cream/70 mb-4">
                <strong className="text-cream">10.1 Prohibited Items:</strong> The following may not be listed on the Platform:
              </p>
              <ul className="list-disc list-inside text-cream/70 space-y-1 ml-4 mb-4">
                <li>Illegal items or substances</li>
                <li>Weapons or ammunition</li>
                <li>Counterfeit or stolen goods</li>
                <li>Hazardous materials</li>
                <li>Items that infringe intellectual property rights</li>
                <li>Animals</li>
                <li>Services (Competitions are for physical goods only)</li>
              </ul>

              <p className="text-cream/70 mb-4">
                <strong className="text-cream">10.2 Prohibited Conduct:</strong> Users must not:
              </p>
              <ul className="list-disc list-inside text-cream/70 space-y-1 ml-4 mb-4">
                <li>Create multiple accounts</li>
                <li>Use automated systems to enter Competitions</li>
                <li>Collude with other users to manipulate Competitions</li>
                <li>Harass or abuse other users</li>
                <li>Circumvent Platform security measures</li>
                <li>Use the Platform for money laundering or fraud</li>
              </ul>

              <p className="text-cream/70">
                <strong className="text-cream">10.3 Enforcement:</strong> Violation of prohibited items or conduct rules may result in immediate account termination and forfeiture of any pending payments or Entries.
              </p>
            </section>

            {/* Section 11 */}
            <section id="privacy">
              <h2 className="text-xl font-semibold text-cream mb-4">11. Privacy and Data Protection</h2>

              <p className="text-cream/70 mb-4">
                <strong className="text-cream">11.1 Data Processing:</strong> We process personal data in accordance with our <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>. By using the Platform, you consent to such processing.
              </p>

              <p className="text-cream/70 mb-4">
                <strong className="text-cream">11.2 Information Sharing:</strong> Winner contact information is shared with the relevant Seller only for the purpose of arranging Handover. Seller pickup addresses are shared with Winners only.
              </p>

              <p className="text-cream/70">
                <strong className="text-cream">11.3 Marketing:</strong> With your consent, we may send marketing communications about Competitions and Platform features. You can opt out at any time.
              </p>
            </section>

            {/* Section 12 */}
            <section id="changes">
              <h2 className="text-xl font-semibold text-cream mb-4">12. Changes to Terms</h2>

              <p className="text-cream/70 mb-4">
                <strong className="text-cream">12.1 Amendments:</strong> We may amend these Terms at any time. Changes will be posted on this page with an updated "Last updated" date.
              </p>

              <p className="text-cream/70 mb-4">
                <strong className="text-cream">12.2 Notification:</strong> For material changes, we will notify registered users via email at least 14 days before the changes take effect.
              </p>

              <p className="text-cream/70">
                <strong className="text-cream">12.3 Continued Use:</strong> Your continued use of the Platform after changes take effect constitutes acceptance of the revised Terms.
              </p>
            </section>

            {/* Section 13 */}
            <section id="governing-law">
              <h2 className="text-xl font-semibold text-cream mb-4">13. Governing Law</h2>

              <p className="text-cream/70 mb-4">
                <strong className="text-cream">13.1 Jurisdiction:</strong> These Terms are governed by the laws of the Republic of Ireland. Any disputes shall be subject to the exclusive jurisdiction of the Irish courts.
              </p>

              <p className="text-cream/70">
                <strong className="text-cream">13.2 Compliance:</strong> Users are responsible for ensuring their participation complies with applicable laws in their jurisdiction.
              </p>
            </section>

            {/* Section 14 */}
            <section id="contact">
              <h2 className="text-xl font-semibold text-cream mb-4">14. Contact</h2>

              <p className="text-cream/70 mb-4">
                For questions about these Terms and Conditions, please contact us at:
              </p>
              <p className="text-cream/70">
                Email: <a href="mailto:support@raflr.ie" className="text-primary hover:underline">support@raflr.ie</a>
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
